/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/no-array-index-key */
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { useEffect, useState } from 'react';
import DataTable from '../../juristec-ui/core/DataTable';
import { formatDateTime } from '../../juristec-ui/utils/functions/lab';
import Tooltip from '../../juristec-ui/core/Tooltip';
import Avatar from '../../juristec-ui/core/Avatar';
import Badge from '../../juristec-ui/core/Badge';
import UserOptions from '../UsersOptions';
import DataCards from '../../juristec-ui/core/DataCards';
import TableFilters from '../Popovers/TableFilters';
import useDeviceType from '../../juristec-ui/hooks/useDeviceType';
import {
  UserInfoContainer,
  UserInfoText,
  UsersTable,
  UsersTableOverFlow,
} from './styled/UsersList.styled';

/**
 * A component that displays the users info and options
 */
const UsersList = ({
  users,
  currentUser,
  promoteUser,
  demoteUser,
  toggleUserState,
  deleteUser,
  editUser,
  openUserHistory,
  groups,
  atModal = false,
}) => {
  const device = useDeviceType();
  const theme = useTheme();

  const DataListingComponent = device === 'phone' ? DataCards : DataTable;
  const [filteredUsers, setFilteredUsers] = useState(users.sort((a, b) => a.email.localeCompare(b.email)));

  const [filters, setFilters] = useState({
    groups: { isOpen: false, selected: [], isDirty: false },
  });

  const getFilteredUsers = () => users.filter((user) => {
    if (filters.groups.selected.length > 0) {
      if (!user.groups) return false;
      return user.groups.some((group) => filters.groups.selected.includes(group));
    }
    return true;
  });

  useEffect(() => {
    const filtered = getFilteredUsers();
    setFilteredUsers(filtered);
  }, [users, filters.groups.selected]);

  const toggleFilter = (field, state) => {
    setFilters((f) => ({
      ...f,
      [field]: {
        ...(f[field] || { selected: [], isDirty: false }),
        isOpen: state !== undefined ? state : !f[field]?.isOpen,
      },
    }));
  };

  const handleFilter = (field, selected) => {
    setFilters((f) => ({
      ...f,
      [field]: {
        ...f[field],
        selected,
        isDirty: true,
      },
    }));
  };

  const moreOptions = (user) => (
    // TODO Alterar essa comparacao quando o historico for adicionado
    <Tooltip text="Mais opções">
      <UserOptions
        user={user}
        currentUser={currentUser}
        promoteUser={promoteUser}
        demoteUser={demoteUser}
        toggleUserState={toggleUserState}
        deleteUser={deleteUser}
        editUser={editUser}
        openUserHistory={openUserHistory}
      />
    </Tooltip>
  );

  const userInfo = (user) => (
    <UserInfoContainer>
      <Avatar name={user.name} src={user.photoUrl || null} size="medium" />
      <UserInfoText>
        <h4>{user.name}</h4>
        <span>{user.email}</span>
      </UserInfoText>
    </UserInfoContainer>
  );

  const getGroups = (user) => (
    <div style={{
      display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center',
    }}
    >
      {user?.groups?.length > 0 ? (
        user.groups.map((group, index) => (
          <Badge
            key={`${group}-${index}`}
            size="small"
            color="info"
          >
            {groups.find((g) => g.id === group)?.label || group}
          </Badge>
        ))
      ) : (
        <span>-</span>
      )}
    </div>
  );

  const loginDateTime = (datetime) => (
    datetime ? formatDateTime(datetime, { time: 'half' }) : 'Não consta'
  );

  const userState = (disabled) => (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Badge size="small" color={disabled ? 'error' : 'success'}>
        {disabled ? 'Inativo' : 'Ativo'}
      </Badge>
    </div>
  );

  const columns = [
    {
      field: 'name',
      role: 'primaryInfo',
      label: 'Usuário',
      sortable: true,
      valueGetter: (param) => userInfo(param),
    },
    {
      field: 'roleLabel',
      role: 'extraInfo',
      label: 'Permissão',
      sortable: true,
    },
    device === 'desktop' && currentUser.role !== 'scientist'
    && {
      field: 'groups',
      role: 'extraInfo',
      label: 'Grupo',
      sortable: true,
      valueGetter: (param) => getGroups(param),
      tools: !atModal && (
        <TableFilters
          field="groups"
          isOpen={filters?.groups?.isOpen || false}
          toggle={toggleFilter}
          options={groups.map((group) => ({
            label: group.label,
            value: group.id,
            id: group.id,
          }))}
          selected={filters?.groups?.selected || []}
          handleFilter={handleFilter}
        />
      ),
    },
    {
      field: 'scientistName',
      role: 'extraInfo',
      label: 'Cientista responsável',
      sortable: true,
    },
    {
      field: 'lastLogin',
      role: 'extraInfo',
      label: 'Último login',
      sortable: true,
      valueGetter: (param) => loginDateTime(param.lastLogin),
    },
    {
      field: 'disabled',
      role: 'extraInfo',
      label: 'Status',
      valueGetter: (param) => userState(param.disabled),
    },
    !atModal && {
      field: 'options',
      role: 'optionsBtn',
      label: '',
      valueGetter: (param) => moreOptions(param),
    },
  ];

  return (
    <UsersTable>
      <UsersTableOverFlow>
        <DataListingComponent
          columns={columns}
          rowData={filteredUsers}
          strippedRowsColor={theme.containerOdd}
          defaultSortField="roleLabel"
          defaultSortOrder="ascending"
          headerColor="transparent"
          rowColor={theme.tableBackground}
          theadStyle={{
            position: 'sticky',
            top: 0,
            zIndex: !atModal ? 1 : 1050,
            backgroundColor: atModal ? theme.transparent : theme.background,
          }}
          toolsPosition="start"
        />
      </UsersTableOverFlow>
    </UsersTable>
  );
};

UsersList.propTypes = {
  /**
   * Array with the data of the users
   */
  users: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  /**
   * Object with the current user data
   */
  currentUser: PropTypes.objectOf(PropTypes.any).isRequired,
  /**
   * Function for promoting the user
   */
  promoteUser: PropTypes.func.isRequired,
  /**
   * Function for demoting the user
   */
  demoteUser: PropTypes.func.isRequired,
  /**
   * Function to toggle the activation state of the user
   */
  toggleUserState: PropTypes.func.isRequired,
  /**
   * Function for deleting a user
   */
  deleteUser: PropTypes.func.isRequired,
  /**
   * Function for editing a user
   */
  editUser: PropTypes.func.isRequired,
  /**
   * Function to show the history associated with the user
   */
  openUserHistory: PropTypes.func.isRequired,
  /**
   * Array of available groups
   */
  groups: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  /**
   * Indicates if component is inside a modal
  */
  atModal: PropTypes.bool,
};

export default UsersList;
